import { LogUserActivity } from '@/apis/users/requests'
import {
  FrameCornerIcon,
  SparkleIcon,
  StackIcon,
} from '@/app/common/icons/general'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { PageRouterState } from '@/app/pages/family_application/student_details/StudentBirthDate.tsx'
import { Button } from '@/common/components/Button'
import { AppRoutePaths } from '@/routing/route_paths'
import SampleBirthCertificate from '@assets/utah-birth-certificate.jpg'
import SampleBirthCertificateEs from '@assets/utah-birth-certificate_es.jpg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { LOG_TYPES } from '../constants'
import { useTypesafeStudent } from './useTypesafeStudent'

const NoBirthCert: PageRouterState = {
  applicant_doesnt_have_birth_cert: true,
  manually_entered_birth_date: ''
} as const

export const StudentCapInstructPage = () => {
  const { t: translate, i18n } = useTranslation()
  const navigateTo = useNavigate()
  const { fullName } = useTypesafeStudent()
  const [isOpen, setIsOpen] = useState(false)
  const openModal = () => setIsOpen(true)
  const closeModal = () => setIsOpen(false)

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('student_identity_verification')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <h2 className="text-center text-xl text-gray-900 my-10 mb-3 sm:mb-2">
          {translate('we_need_student_birth_certificate').replace(':student_name', fullName)}.
        </h2>
        <img
          alt={translate('birth_certificate')}
          width="100%"
          src={i18n.language == 'es' ? SampleBirthCertificateEs : SampleBirthCertificate}
          onClick={openModal}
          className="mx-auto h-auto mt-5 hover:cursor-pointer"
        />
        <div className="text-sm font-small text-gray-500 mt-3 sm:mb-2 text-center">
          {translate('tap_to_enlarge')}.
        </div>

        <h2 className="text-center text-xl text-gray-900 my-10 mb-3 sm:mb-2">
          {translate('birth_certificate_capture_instructions')}
        </h2>

        <section aria-describedby='position-document' className="mt-4">
          <div className="flex items-center">
            <div className="w-5 flex justify-center">
              <FrameCornerIcon />
            </div>
            <div id='position-document' className="ml-4 text-sm">
              {translate('position_your_document')}
            </div>
          </div>
        </section>

        <section aria-describedby='position-document' className="mt-6">
          <div className="flex items-center">
            <div className="w-5 flex justify-center">
              <StackIcon />
            </div>
            <div className="ml-4 text-sm">
              {translate('place_it_flat_surface')}
            </div>
          </div>
        </section>

        <section aria-describedby='position-document' className="mt-6">
          <div className="flex items-center">
            <div className="w-5 flex justify-center">
              <SparkleIcon />
            </div>
            <div className="ml-4 text-sm">
              {translate('avoid_glare_shaking')}
            </div>
          </div>
        </section>

        <div className="mt-6">
          <div className="flex items-center">
            <div className="w-5 flex justify-center">
              <FrameCornerIcon />
            </div>
            <div className="ml-4 text-sm">
              {translate('camera_directly_above')}
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="relative">
              <img
                src={i18n.language == 'es' ? SampleBirthCertificateEs : SampleBirthCertificate}
                alt={translate('birth_certificate')}
                className="max-w-full max-h-full"
              />

              <button
                className="absolute top-2 right-2 text-white text-2xl font-bold"
                onClick={closeModal}
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <div className="mt-5 w-full flex flex-col text-center space-y-6">
          <Button
            type='button'
            onClick={() => {
              navigateTo(
                AppRoutePaths.family_application.student_details.capture
              )
            }}
            className="h-9"
          >
            {translate('continue')}
          </Button>
          <Link
            className="text-sm font-medium link text-primary cursor-pointer underline"
            to={
              AppRoutePaths.family_application.student_details.additional_details
            }
            state={NoBirthCert}
            onClick={() => {
              LogUserActivity({
                log_type: LOG_TYPES.FAILED,
                suffix_text: `To identify ${fullName} (Student) with Birth Certification`
              }) // Log page view        
            }}
            aria-describedby='i_dont_have_document_link_desc'
          >
            {translate('i_dont_have')} {translate('student_birth_certificate').replace(':student_name', fullName)}
          </Link>
          <h2 id="i_dont_have_document_link_desc" className="sr-only">{translate('i_dont_have_document_link_desc')}</h2>
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
